import { useCallback, useEffect, useMemo, useReducer } from "react";
import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { InputForm } from "src/components/common/form/input/InputForm";
import { DropDownForm } from "src/components/common/form/DropdownForm";

import ModalWrapper from "../ModalWrapper";
import { SearchForm } from "../SearchForm";
import { SearchTable } from "./SearchTable";

import { isBankacct, isCompany, isCustomer, isEmployee, isEquipment, isInspect, isItem, isMenu, isMenuAuth, isOrg, isUser, isWarehouse, isWorkplace } from "./SearchTableContent";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { useCodeSearch } from "src/hooks/useCodeSearch";

// 공통 타입과 유틸리티 import (새로운 구조)
import { SearchableDto, CodeType } from "src/types/base";
import { getCodeTypeFromTitle, getUiConfig } from "src/utils/categoryUtils";
import { filterSearchableData } from "src/utils/searchHelpers";
import { 
	CodeSearchModalProps, 
	SearchState, 
	SearchAction, 
	FilterField, 
	CategoryFilter, 
	CategoryFilters,
	FocusMoveConfig,
	SelectionEvent
} from "src/types/code-search";

// ===== 검색 상태 관리 =====
// SearchState와 SearchAction은 이미 types/code-search.ts에서 정의됨

const searchReducer = (state: SearchState, action: SearchAction): SearchState => {
	switch (action.type) {
		case 'SET_SEARCH':
			return { ...state, search: action.payload };
		case 'SET_FILTERS':
			return { ...state, filters: action.payload };
		case 'SET_GRID_DATA':
			return { ...state, gridData: action.payload };
		case 'SET_FILTERED_LIST':
			return { ...state, filteredList: action.payload };
		case 'SET_SELECTED':
			return { ...state, selected: action.payload };
		case 'SET_LOADING':
			return { ...state, isLoading: action.payload };
		case 'SET_ERROR':
			return { ...state, errorMessage: action.payload };
		case 'INITIALIZE':
			return { 
				...state, 
				search: action.payload.search,
				filters: action.payload.filters
			};
		case 'RESET':
			return {
				search: '',
				filters: {},
				gridData: [],
				filteredList: [],
				selected: undefined,
				isLoading: false,
				errorMessage: undefined
			};
		default:
			return state;
	}
};

const initialState: SearchState = {
	search: '',
	filters: {},
	gridData: [],
	filteredList: [],
	selected: undefined,
	isLoading: false,
	errorMessage: undefined
};

// ===== 커스텀 훅: 검색 로직 통합 =====
const useSearchLogic = (codeSearchModalProps: CodeSearchModalProps | null) => {
	//state < 모달 상태
	//dispatch < 모달 상태 변경
	const [state, dispatch] = useReducer(searchReducer, initialState);
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	
	const { 
		searchForModalByType
	} = useCodeSearch();

	// 현재 카테고리의 CodeType 가져오기 
	const currentCodeType = useMemo((): CodeType | null => {
		if (!codeSearchModalProps?.category) return null;
		return getCodeTypeFromTitle(codeSearchModalProps.category) || null;
	}, [codeSearchModalProps?.category]);

	// 검색 실행 함수 (디바운싱 적용)
	const executeSearch = useCallback(async () => {
		const codeType = currentCodeType;
		if (!codeType) return;

		try {
			dispatch({ type: 'SET_LOADING', payload: true });
			dispatch({ type: 'SET_ERROR', payload: '' });
			setLoadingSpinner(true);
			
			const responseData = await searchForModalByType(
				codeType,
				state.search || '',  // 빈 문자열로 전달
				state.filters
			);
			
			const dataArray = Array.isArray(responseData) ? responseData : [];
			dispatch({ type: 'SET_GRID_DATA', payload: dataArray });
			dispatch({ type: 'SET_FILTERED_LIST', payload: dataArray });
		} catch (error) {
			console.error("Failed to fetch data", error);
			const errorMessage = error instanceof Error ? error.message : '데이터를 불러오는데 실패했습니다.';
			dispatch({ type: 'SET_ERROR', payload: errorMessage });
		} finally {
			dispatch({ type: 'SET_LOADING', payload: false });
			setLoadingSpinner(false);
		}
	}, [currentCodeType, state.search, state.filters, searchForModalByType, setLoadingSpinner]);

	// 클라이언트 필터링 함수
	const applyClientFiltering = useCallback(() => {
		const filtered = filterSearchableData(state.gridData, state.search);
		dispatch({ type: 'SET_FILTERED_LIST', payload: filtered });
	}, [state.gridData, state.search]);

	// 초기화 함수
	const initializeSearch = useCallback(() => {
		if (!codeSearchModalProps) return;

		const extraFilter = codeSearchModalProps.extraFilter || {};
		const search = codeSearchModalProps.search || '';
		
		let filters: Record<string, string> = {};
		
		// 카테고리별 기본 필터 설정
		const defaultFiltersByCategory: Record<string, Record<string, string>> = {
			item: {
				itemAccount: 'all',
				itemGroup: 'all',
				brandCd: 'all'
			},
			customer: {
				areaCd: 'all'
			}
		};

		// 현재 카테고리의 기본 필터 가져오기
		const defaultFilters = defaultFiltersByCategory[currentCodeType || ''] || {};
		
		// extraFilter와 병합하여 최종 필터 생성
		const processedExtraFilter = Object.fromEntries(
			Object.entries(extraFilter).map(([key, value]) => [key, String(value || 'all')])
		);
		
		filters = {
			...defaultFilters,
			...processedExtraFilter
		};

		dispatch({ type: 'INITIALIZE', payload: { search, filters } });
	}, [codeSearchModalProps, currentCodeType]);

	return {
		state,
		dispatch,
		currentCodeType,
		executeSearch,
		applyClientFiltering,
		initializeSearch
	};
};

// ===== 커스텀 훅: 모달 생명주기 관리 =====
const useModalLifecycle = (codeSearchModalProps: CodeSearchModalProps | null, codeSearchModalVisible: boolean) => {
	// 키보드 이벤트 관리
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Enter") {
				// 엔터 키 처리 로직은 별도로 구현
			}
		};

		if (codeSearchModalVisible) {
			window.addEventListener("keydown", handleKeyDown);
		}

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [codeSearchModalVisible]);

	// 모달 초기화
	useEffect(() => {
		if (codeSearchModalProps?.category) {
			// 카테고리 변경 시 초기화 로직
		}
	}, [codeSearchModalProps?.category]);
};

// 이벤트 타입 구분을 위한 인터페이스는 이미 types/code-search.ts에서 정의됨

// 타입 가드 매핑
const TYPE_GUARDS: Record<CodeType, (item: SearchableDto) => boolean> = {
	item: isItem,
	customer: isCustomer,
	organization: isOrg,
	employee: isEmployee,
	user: isUser,
	menu: isMenu,
	menuAuth: isMenuAuth,
	workplace: isWorkplace,
	warehouse: isWarehouse,
	bankacct: isBankacct,
	inspect: isInspect,
	equipment: isEquipment,
	company: isCompany
};

export const CodeSearchModal = () => {
	const { codeSearchModalProps, codeSearchModalVisible, setCodeSearchModalVisible } = useUiuxStore();
	const { brandCdList, itemGroupList, itemAccountList } = useCommonCodeStore();

	// 검색 로직 통합 훅 사용
	const {
		state,
		dispatch,
		currentCodeType,
		executeSearch,
		applyClientFiltering,
		initializeSearch
	} = useSearchLogic(codeSearchModalProps || null);

	// 모달 생명주기 관리 훅 사용
	useModalLifecycle(codeSearchModalProps || null, codeSearchModalVisible);

	// 카테고리별 필터 설정 (새로운 구조 기반)
	const categoryFilters: CategoryFilters = useMemo(() => {
		return {
			'거래처': {
				fields: [], // 거래처는 필터가 없음 (지역별 검색은 API에서 처리)
				searchField: 'customCd',
				searchLabel: '검색어'
			},
			'조직': {
				fields: [],
				searchField: 'orgCd',
				searchLabel: '검색어'
			},
			'사원': {
				fields: [],
				searchField: 'empNo',
				searchLabel: '검색어'
			},
			'시험자': {
				fields: [],
				searchField: 'empNo',
				searchLabel: '검색어'
			},
			'발주자': {
				fields: [],
				searchField: 'empNo',
				searchLabel: '검색어'
			},
			'담당자': {
				fields: [],
				searchField: 'empNo',
				searchLabel: '검색어'
			},
			'작업자': {
				fields: [],
				searchField: 'empNo',
				searchLabel: '검색어'
			},
			'사용자': {
				fields: [],
				searchField: 'userId',
				searchLabel: '검색어'
			},
			'메뉴': {
				fields: [],
				searchField: 'menuCd',
				searchLabel: '검색어'
			},
			'메뉴권한': {
				fields: [],
				searchField: 'codeCd',
				searchLabel: '검색어'
			}
		};
	}, [itemAccountList, itemGroupList, brandCdList]);

	// 포커스 이동 함수 추가
	const moveFocusToNextCell = useCallback((config: FocusMoveConfig = {}) => {
		const { nextColumnKey, delay = 0 } = config;
		
		if (!nextColumnKey || !codeSearchModalProps?.gridRef?.current?.api) return;

		setTimeout(() => {
			const api = codeSearchModalProps.gridRef?.current?.api;
			if (!api) return;

			// 현재 선택된 행의 인덱스 찾기
			let targetRowIndex = -1;
			api.forEachNode((node: any) => {
				if (node.data && node.data.valueIndex === codeSearchModalProps?.rowIndex && node.rowIndex !== null) {
					targetRowIndex = node.rowIndex;
				}
			});

			if (targetRowIndex !== -1) {
				api.startEditingCell({
					rowIndex: targetRowIndex,
					colKey: nextColumnKey,
				});
			}
		}, delay);
	}, [codeSearchModalProps]);

	// 선택 이벤트 처리 함수 추가
	const handleSelection = useCallback((selectedItem: SearchableDto, event?: SelectionEvent) => {
		if (!codeSearchModalProps?.onConfirm) return;

		// 엔터(키보드) 또는 마우스 더블클릭으로 선택한 경우에만 포커스 이동
		const shouldMoveFocus = !event || (event && (event.type === 'keyboard' || event.detail === 2));
		
		// onConfirm 콜백 실행
		codeSearchModalProps.onConfirm(selectedItem, event);

		// 포커스 이동이 필요한 경우
		if (shouldMoveFocus && codeSearchModalProps.focusMoveConfig) {
			moveFocusToNextCell(codeSearchModalProps.focusMoveConfig);
		}
	}, [codeSearchModalProps, moveFocusToNextCell]);

	// ===== 통합된 useEffect들 =====

	// 1. 검색 조건 변경 시 API 호출 (디바운싱 적용)
	useEffect(() => {
		if (!currentCodeType) return;

		const timeoutId = setTimeout(() => {
			executeSearch();
		}, 300); // 300ms 디바운싱

		return () => clearTimeout(timeoutId);
	}, [currentCodeType, state.search, state.filters, executeSearch]);

	// 2. 그리드 데이터 변경 시 클라이언트 필터링
	useEffect(() => {
		applyClientFiltering();
	}, [state.gridData, state.search, applyClientFiltering]);

	// 3. 자동 선택: 검색 결과가 1개일 때 자동으로 선택하고 모달 닫기
	useEffect(() => {
		if (!codeSearchModalProps?.autoSelectSingle) return;
		if (!state.filteredList || state.filteredList.length !== 1) return;
		
		const singleItem = state.filteredList[0];
		const { onConfirm } = codeSearchModalProps;
		
		// 자동 선택 실행
		if (onConfirm && singleItem) {
			onConfirm(singleItem);
			setCodeSearchModalVisible(false);
		}
	}, [state.filteredList, codeSearchModalProps, setCodeSearchModalVisible]);

	// 4. 모달 props 변경 시 초기화
	useEffect(() => {
		if (codeSearchModalProps) {
			initializeSearch();
		}
	}, [codeSearchModalProps, initializeSearch]);

	// ===== 이벤트 핸들러들 =====

	const handleSearchChange = useCallback((value: string) => {
		dispatch({ type: 'SET_SEARCH', payload: value });
	}, []);

	const handleFilterChange = useCallback((key: string, value: string) => {
		dispatch({ type: 'SET_FILTERS', payload: { ...state.filters, [key]: value } });
	}, [state.filters]);

	const handleItemSelect = useCallback((item: SearchableDto | undefined) => {
		dispatch({ type: 'SET_SELECTED', payload: item });
	}, []);

	const handleConfirmClick = useCallback(() => {
		if (!codeSearchModalProps) return;
		const { 
			category,
			formName,
			isInfo,
			rowIndex,
			onClear,
			onConfirm = () => { alert('모달 코드 등록후 행동할 동작이 없습니다.'); },
			onCloseConfirm = () => { /* alert('모달 코드 등록후 행동할 동작이 없습니다.'); */ },
			processType_focus = 'normal',
			wrapperOption = {
				size: 'half',
				width: 50,
				overlay: true
			}
		} = codeSearchModalProps;
		
		if (!state.selected) {
			alert('선택된 항목이 없습니다.');
			return;
		}
		// 선택 처리 및 포커스 이동 (버튼 클릭도 포커스 이동 대상)
		handleSelection(state.selected);

		setCodeSearchModalVisible(false);
		if (onCloseConfirm) onCloseConfirm(state.selected);
	}, [
		state.selected,
		codeSearchModalProps,
		setCodeSearchModalVisible,
		handleSelection,
		currentCodeType
	]);

	// 동적 필터 렌더링 함수
	const renderFilters = (category: string) => {
		const filterConfig = categoryFilters[category];
		if (!filterConfig) return null;

		return (
			<div className="w-full h-[10%] flex items-center px-[8px]">
				<div className="w-full flex items-center gap-[8px]">
					{/* 검색어 입력 */}
					<InputForm
						title={filterConfig.searchLabel}
						value={state.search}
						editable={true}
						wrapperStyle={{
							wrapperStyle: "flex-1 mb-[5px]",
							titleStyle: "w-[40px] min-w-[40px] text-[16px]",
							inputStyle: "flex-1 text-[16px]"
						}}
						name="search"
						onChange={(e) => {
							handleSearchChange(e.target.value);
							// 검색어 입력 시 자동으로 조회
							setTimeout(() => executeSearch(), 300);
						}}
						onKeyDown={(e) => e.key === 'Enter' && executeSearch()}
						placeholder="100-1234"
					/>

					{/* 동적 필터 필드들 */}
					{filterConfig.fields.map(field => (
						<DropDownForm
							key={field.key}
							title={field.label}
							value={state.filters[field.key] || 'all'}
							data={field.data}
							wrapperStyle={{
								wrapperStyle: "mb-[5px]",
								titleStyle: "w-[60px] min-w-[60px]",
								inputStyle: "w-[120px]"
							}}
							onChange={(e) => handleFilterChange(field.key, e.target.value)}
						/>
					))}
				</div>
			</div>
		);
	};

	// 범용 테이블 렌더링 함수
	const renderTable = () => {
		if (!currentCodeType) {
			return <div>지원하지 않는 카테고리입니다.</div>;
		}

		const typeGuard = TYPE_GUARDS[currentCodeType];
		const filteredData = state.filteredList.filter(typeGuard);
		const selectedData = state.selected && typeGuard(state.selected) ? state.selected : undefined;

		return (
			<SearchTable
				codeType={currentCodeType}
				formName={codeSearchModalProps?.formName || ''}
				data={filteredData}
				selected={selectedData}
				setSelected={handleItemSelect}
				onConfirm={(item: any) => handleSelection(item)}
				onCloseConfirm={codeSearchModalProps?.onCloseConfirm}
				processType_focus={codeSearchModalProps?.processType_focus || 'normal'}
			/>
		);
	};

	// 에러 메시지 렌더링
	const renderErrorMessage = () => {
		if (!state.errorMessage) return null;
		
		return (
			<div className="px-4 py-2 bg-red-50 text-red-600 text-sm border border-red-200 rounded">
				{state.errorMessage}
			</div>
		);
	};

	// 모달이 닫혀있으면 렌더링하지 않음
	if (!codeSearchModalVisible) return null;

	return (
	<ModalWrapper 
		size={codeSearchModalProps?.wrapperOption?.size || 'md'}
		width={codeSearchModalProps?.wrapperOption?.width || 70}
		overlay={codeSearchModalProps?.wrapperOption?.overlay || true}
		onOverlayClick={() => setCodeSearchModalVisible(false)}
		onEscKeyDown={() => {
			setCodeSearchModalVisible(false);
		}}
	>
		{/* codesearchModalProps : {JSON.stringify(codeSearchModalProps)} */}
		<div className="w-full h-[10%] flex items-center px-[24px] bg-gray-100">
			<p className="font-bold text-[16px]">
				코드 검색 ({codeSearchModalProps?.category} 정보)
			</p>
		</div>
		
		{/* 동적 필터 렌더링 */}
		{renderFilters(codeSearchModalProps?.category || '')}
		
	{/* 기존 검색 폼 (필터가 없는 카테고리일 때만 표시) */}
	{!categoryFilters[codeSearchModalProps?.category || ''] && (
		<div className="w-full h-[10%] flex items-center px-[8px]">
			<SearchForm 
				value={state.search} 
				setSearch={handleSearchChange} 
				onSearch={() => { }} 
				autoFocus={codeSearchModalProps?.autoFocus !== false}
			/>
		</div>
	)}

		{/* 에러 메시지 표시 */}
		{renderErrorMessage()}

		{/* 검색 결과 개수 표시 */}
		{/* <div className="px-4 py-2 bg-gray-50 text-sm text-gray-600">
			검색 결과: {state.filteredList.length}건
			</div> */}

		<div className="w-full h-[70%] px-[8px]">
			{/* 동적 테이블 렌더링 */}
			{renderTable()}
		</div>

		<div className="w-full h-[10%] flex justify-end items-center gap-[8px] px-[8px]">
			{/* 검증 : {codeSearchModalProps?.onClear ? '있음' : '없음'} */}
			
			{codeSearchModalProps?.onClear && (
			<button className={`text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold`}
				onClick={() => {
					if (codeSearchModalProps?.onClear) codeSearchModalProps?.onClear();
					setCodeSearchModalVisible(false);
				}}>
				비우기
			</button>
			)}
			<button className={`text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold`}
				onClick={handleConfirmClick}>
				확인
			</button>
			<button className={`text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold`}
				onClick={() => setCodeSearchModalVisible(false)}>
				취소
			</button>
		</div>
	</ModalWrapper>
	)
}